import React, { Component } from 'react'
import { inject, observer } from 'mobx-react';
import { observable } from 'mobx';
import { Alert, Intent } from '@blueprintjs/core';
import Toast from 'helpers/toaster';
import * as DataList from 'components/DataList';
import TaxModal from 'components/Taxes/TaxModal';

// Layout shared by the list and its loading skeleton (pages/taxes/index.js).
export const TAX_COLUMNS = [
  { area: 'name', label: 'Tax', width: 'minmax(200px, 1.6fr)', bone: 'single' },
  { area: 'rate', label: 'Rate', width: 'minmax(90px, 0.6fr)', bone: 'short', icon: 'percentage' },
  { area: 'accounting', label: 'Accounting', width: 'minmax(170px, 1fr)', bone: 'pill', icon: 'link' },
  { area: 'actions', label: '', width: '72px', bone: 'none' },
];

export const TAX_CARDS = {
  columns: 'minmax(0, 1fr) auto',
  areas: ['name actions', 'rate rate', 'accounting accounting'],
};

export const TAX_MIN_TABLE_WIDTH = 560;

const isLinked = (tax) => {
  const mappings = tax.attributes.external_mappings;
  return Array.isArray(mappings) ? mappings.length > 0 : Boolean(mappings);
};

// "13", "13.0", 13 -> "13%"; "8.875" stays as typed.
const rateLabel = (value) => {
  if (value === null || value === undefined || value === '') return '–';
  const number = Number(value);
  return `${Number.isFinite(number) ? number.toString() : value}%`;
};

// The taxes list (Settings > Taxes): name, rate, whether it's linked to the
// accounting app; edit opens the tax panel, delete asks first.
@inject('taxStore', 'quickbooksStore', 'xeroStore') @observer
class TaxesTable extends Component {
  @observable deleting = null; // the tax waiting for delete confirmation
  @observable isTaxDialogVisible = false;

  openEdit(tax) {
    this.props.taxStore.tax.object = tax.attributes;
    this.isTaxDialogVisible = true;
    this.props.quickbooksStore.loadQuickbooksTaxCodes();
    this.props.xeroStore.loadXeroTaxCodes();
  }

  confirmDelete = () => {
    const tax = this.deleting;
    this.deleting = null;
    this.props.taxStore.delete(tax)
      .then(() => {
        Toast.show({ message: `${tax.attributes.name} deleted.`, intent: Intent.SUCCESS });
      })
      .catch(() => {
        Toast.show({ message: `Couldn't delete ${tax.attributes.name}. Try again.`, intent: Intent.DANGER });
      });
  };

  render() {
    const { objects } = this.props.taxStore.taxes;
    const { onAdd } = this.props;
    if (objects.length === 0) {
      return (
        <DataList.Empty
          noun="taxes"
          title="No taxes yet"
          message="Add the rates you charge, like GST or a state sales tax, so you can put them on estimates and invoices."
          extraAction={onAdd && { label: 'Add tax', icon: 'add', onClick: onAdd, primary: true }}
        />
      );
    }
    const deleting = this.deleting && this.deleting.attributes;
    return (
      <React.Fragment>
        <DataList.List
          columns={TAX_COLUMNS}
          cards={TAX_CARDS}
          minTableWidth={TAX_MIN_TABLE_WIDTH}
          total={objects.length}
          noun="taxes"
        >
          {objects.map((tax) => (
            <DataList.Row key={tax.id}>
              <DataList.Cell area="name">
                <div className="sp-name">
                  <DataList.Lines primary={tax.attributes.name} />
                  {tax.attributes.is_default && <DataList.Pill tone="blue">Default</DataList.Pill>}
                </div>
              </DataList.Cell>
              <DataList.Cell area="rate">
                <span className="sp-number">{rateLabel(tax.attributes.percentage_value)}</span>
              </DataList.Cell>
              <DataList.Cell area="accounting">
                {isLinked(tax)
                  ? <DataList.Pill tone="green" icon="tick">Linked</DataList.Pill>
                  : <DataList.Muted>Not linked</DataList.Muted>}
              </DataList.Cell>
              <DataList.Cell area="actions">
                <DataList.Actions
                  items={[
                    { icon: 'edit', label: `Edit ${tax.attributes.name}`, onClick: () => this.openEdit(tax) },
                    { icon: 'trash', label: `Delete ${tax.attributes.name}`, onClick: () => { this.deleting = tax; }, intent: Intent.DANGER },
                  ]}
                />
              </DataList.Cell>
            </DataList.Row>
          ))}
        </DataList.List>

        <Alert
          isOpen={Boolean(this.deleting)}
          icon="trash"
          intent={Intent.DANGER}
          cancelButtonText="Cancel"
          confirmButtonText="Delete tax"
          onCancel={() => { this.deleting = null; }}
          onConfirm={this.confirmDelete}
        >
          <strong>{deleting ? `Delete ${deleting.name}?` : 'Delete this tax?'}</strong>
          <p>It won't be offered on new estimates and invoices.</p>
        </Alert>
        <TaxModal
          isOpen={this.isTaxDialogVisible}
          title="Update Tax"
          edit={true}
          onClose={() => { this.isTaxDialogVisible = false; }}
          onSuccess={() => { this.isTaxDialogVisible = false; }}
        />
      </React.Fragment>
    );
  }
}

export default TaxesTable;
